import React, { useMemo } from 'react';
import { formatTimeFromMinutes } from '../utils/helpers';
import { SCHEDULE_START_MINUTE, TOTAL_SCHEDULE_MINUTES } from '../config/constants';

// Define colors for the overlay (using semi-transparent backgrounds)
const AVAILABILITY_COLORS = {
    red: 'bg-red-400/10', // Semi-transparent red
    orange: 'bg-orange-300/50', // Semi-transparent orange
    green: 'bg-transparent' // No overlay for available slots
};

interface AvailabilityStatus {
    teacherStatus: 'red' | 'orange' | 'green';
    groupStatus: 'red' | 'orange' | 'green';
    isRoomOccupied: boolean;
    isRoomValidForSubject: boolean;
    overallStatus: 'red' | 'orange' | 'green';
    conflictReasons: Array<{ reason: string; severity: 'red' | 'orange' | 'green' }>;
    conflictReason?: string;
}

interface AvailabilityTransparentOverlaysProps {
    isAdmin: boolean;
    availabilityOverlayData?: Map<string, AvailabilityStatus> | undefined;
    day: string;
    roomIndexMap: Map<string, number>;
    totalGridContentHeight: number;
    gridHeaderHeight: number;
    checkIntervalMinutes: number;
    KEY_SEPARATOR: string;
}

const AvailabilityTransparentOverlays: React.FC<AvailabilityTransparentOverlaysProps> = ({
    isAdmin,
    availabilityOverlayData,
    day,
    roomIndexMap,
    totalGridContentHeight,
    gridHeaderHeight,
    checkIntervalMinutes,
    KEY_SEPARATOR,
}) => {
    const dayIntervals = useMemo(() => {
        if (!availabilityOverlayData) return [];

        const cells = Array.from<[string, AvailabilityStatus]>(availabilityOverlayData.entries())
            .map(([key, status]) => {
                const [keyDay, roomName, rawTimeMinutes] = key.split(KEY_SEPARATOR);
                const timeMinutes = Number(rawTimeMinutes);
                if (keyDay !== day || !roomName || !Number.isFinite(timeMinutes)) return null;
                return { key, roomName, timeMinutes, status };
            })
            .filter((cell): cell is NonNullable<typeof cell> => cell !== null)
            .sort((a, b) => (
                a.roomName.localeCompare(b.roomName)
                || a.timeMinutes - b.timeMinutes
            ));

        return cells.reduce<Array<{
            key: string;
            roomName: string;
            startMinutes: number;
            endMinutes: number;
            status: AvailabilityStatus;
        }>>((intervals, cell) => {
            const previous = intervals[intervals.length - 1];
            if (
                previous
                && previous.roomName === cell.roomName
                && previous.status.overallStatus === cell.status.overallStatus
                && previous.endMinutes === cell.timeMinutes
            ) {
                previous.endMinutes += checkIntervalMinutes;
                return intervals;
            }

            intervals.push({
                key: cell.key,
                roomName: cell.roomName,
                startMinutes: cell.timeMinutes,
                endMinutes: cell.timeMinutes + checkIntervalMinutes,
                status: cell.status
            });
            return intervals;
        }, []);
    }, [availabilityOverlayData, day, KEY_SEPARATOR, checkIntervalMinutes]);

    if (!isAdmin || !availabilityOverlayData) {
        return null;
    }

    return (
        <>
            {dayIntervals.map(({ key, roomName, startMinutes, endMinutes, status }) => {
                const roomIndex = roomIndexMap.get(roomName);
                if (roomIndex === undefined) return null;

                const topPosition = (((startMinutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * totalGridContentHeight) + gridHeaderHeight;
                const height = (totalGridContentHeight * (endMinutes - startMinutes) / TOTAL_SCHEDULE_MINUTES);
                const minHeightPx = 5;
                const calculatedHeightPx = Math.max(minHeightPx, height);

                return (
                    <div
                        key={key}
                        className={`absolute pointer-events-none ${AVAILABILITY_COLORS[status.overallStatus]} rounded-sm`}
                        title={`${roomName} @ ${formatTimeFromMinutes(startMinutes)}–${formatTimeFromMinutes(endMinutes)}: ${status.conflictReason || status.overallStatus}`}
                        style={{
                            top: `${topPosition}px`,
                            height: `${calculatedHeightPx}px`,
                            gridColumnStart: roomIndex + 2,
                            gridColumnEnd: roomIndex + 3,
                            width: '100%',
                            zIndex: 5
                        }}
                    />
                );
            })}
        </>
    );
};

export default React.memo(AvailabilityTransparentOverlays);